import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../firebase-config';
import { collection, query, where, getDocs, GeoPoint } from 'firebase/firestore';
import { lgBandBatteryPercent } from '../../utils/lgBandBattery';

/** Normalize Firestore adafruit device location (GeoPoint, map, or top-level fields). */
function getAdafruitDocCoordinates(data) {
  const loc = data.latest_location;
  if (loc != null) {
    if (loc instanceof GeoPoint) {
      return { lat: loc.latitude, lng: loc.longitude };
    }
    if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
      return { lat: loc.latitude, lng: loc.longitude };
    }
    if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
      return { lat: loc.lat, lng: loc.lng };
    }
  }
  if (data.latitude != null && data.longitude != null) {
    const lat = Number(data.latitude);
    const lng = Number(data.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng };
    }
  }
  return { lat: 0, lng: 0 };
}

function firestoreValueToIso(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}

/** Milliseconds since epoch from Firestore Timestamp / Date / ISO string, or null. */
function firestoreValueToMillis(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    const t = value.toDate().getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'string') {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

const ADAFRUIT_STALE_AFTER_MS = 2 * 60 * 60 * 1000;

/** Normalize packet message code (07 / 03 / 11) from Firestore string, number, or JSON array string. */
function normalizeAdafruitMessageCode(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = Math.round(value);
    if (n >= 0 && n <= 99) return String(n).padStart(2, '0');
    return null;
  }
  const s = String(value).trim();
  if (s.startsWith('[')) {
    try {
      const arr = JSON.parse(s);
      if (Array.isArray(arr) && arr[0] != null) return normalizeAdafruitMessageCode(arr[0]);
    } catch (_) {
      return null;
    }
    return null;
  }
  if (/^\d{1,2}$/.test(s)) return s.padStart(2, '0');
  if (/^(0[37]|11)$/.test(s)) return s;
  if (s.toUpperCase() === 'SOS') return '11';
  return null;
}

function extractAdafruitMessageCode(data) {
  const fromField = normalizeAdafruitMessageCode(data.message_identifier);
  if (fromField) return fromField;
  return normalizeAdafruitMessageCode(data.latest_message_type);
}

/**
 * Whether the client may read the top-level `adafruit` collection.
 * Block only when viewAdafruit is explicitly false AND the user has no fleet vehicle scope.
 */
export function canViewAdafruitLocations(permissions) {
  if (!permissions || typeof permissions !== 'object') return true;
  if (permissions.viewAdafruit !== false) return true;
  if (permissions.viewVehicles === true) return true;
  if (Array.isArray(permissions.viewVehicles) && permissions.viewVehicles.length > 0) return true;
  return false;
}

function mapAdafruitQueryDoc(doc, orgId) {
  const data = doc.data();
  const firestoreDocId = String(doc.id);
  const mapDeviceId = `lg_band_${firestoreDocId}`;
  const coordinates = getAdafruitDocCoordinates(data);
  const hardwareDeviceId = data.device_id != null ? String(data.device_id) : null;

  let lastSeen = null;
  if (data.latest_timestamp) {
    if (data.latest_timestamp.toDate) {
      lastSeen = data.latest_timestamp.toDate().toISOString();
    } else if (data.latest_timestamp instanceof Date) {
      lastSeen = data.latest_timestamp.toISOString();
    } else if (typeof data.latest_timestamp === 'string') {
      lastSeen = new Date(data.latest_timestamp).toISOString();
    }
  }

  const coordsQ = data.coordinates_quality != null ? String(data.coordinates_quality) : null;
  const isCoordsValid = coordsQ === 'valid';
  const messageCode = extractAdafruitMessageCode(data);
  const batteryPct = lgBandBatteryPercent(data);

  const latestTsMs = firestoreValueToMillis(data.latest_timestamp);
  const lastUpdatedMs = firestoreValueToMillis(data.last_updated);
  const candidateMs = [latestTsMs, lastUpdatedMs].filter((n) => n != null && !Number.isNaN(n));
  const mostRecentDataMs = candidateMs.length > 0 ? Math.max(...candidateMs) : null;
  const isDataStale =
    mostRecentDataMs == null || Date.now() - mostRecentDataMs > ADAFRUIT_STALE_AFTER_MS;
  const status = isCoordsValid && !isDataStale ? 'active' : 'inactive';

  return {
    id: mapDeviceId,
    device_id: mapDeviceId,
    name: data.name || `LG Band ${hardwareDeviceId || firestoreDocId}`,
    displayName: (data.name && String(data.name).trim()) || `LG Band (${hardwareDeviceId || firestoreDocId})`,
    coordinates,
    device_type: 'lg_band',
    assetType: 'lg_band',
    // Band-reported % when available, else a clamped Li-ion curve estimate; null = unknown (not 0).
    battery: batteryPct,
    battery_level: batteryPct,
    latest_battery_pct: data.latest_battery_pct != null ? Number(data.latest_battery_pct) : null,
    status,
    lastSeen,
    deviceId: hardwareDeviceId || firestoreDocId,
    adafruit_firestore_doc_id: firestoreDocId,
    adafruit_hardware_device_id: hardwareDeviceId,
    latest_voltage: data.latest_voltage != null ? Number(data.latest_voltage) : null,
    latest_temperature: data.latest_temperature != null ? Number(data.latest_temperature) : null,
    message_identifier: messageCode,
    latest_message_type: data.latest_message_type != null ? String(data.latest_message_type) : null,
    coordinates_quality: coordsQ,
    coordinates_updated_at_iso: firestoreValueToIso(data.coordinates_updated_at),
    last_updated_iso: firestoreValueToIso(data.last_updated),
    latest_timestamp_iso: lastSeen,
    data_source: data.data_source != null ? String(data.data_source) : null,
    organizationId: data.organizationId || orgId,
    sos_incident: normalizeSosIncident(data.sos_incident),
    Vehicle_data: {}
  };
}

/** Serialise the persisted SOS incident (timestamps -> ISO) for Redux. */
function normalizeSosIncident(inc) {
  if (!inc || typeof inc !== 'object') return null;
  return {
    status: inc.status != null ? String(inc.status) : null,
    opened_at: firestoreValueToIso(inc.opened_at),
    closed_at: firestoreValueToIso(inc.closed_at),
    closed_by: inc.closed_by != null ? String(inc.closed_by) : null,
    outside_geofence: inc.outside_geofence === true,
    geofence_name: inc.geofence_name != null ? String(inc.geofence_name) : null,
    lat: inc.lat != null ? Number(inc.lat) : null,
    lng: inc.lng != null ? Number(inc.lng) : null,
  };
}

/** Map a Firestore QuerySnapshot of `adafruit` docs to dashboard device objects. */
export function mapAdafruitSnapshotToDevices(querySnapshot, orgId) {
  if (!querySnapshot?.docs) return [];
  return querySnapshot.docs.map((doc) => mapAdafruitQueryDoc(doc, orgId));
}

// Create the async thunks
export const fetchTowerLocations = createAsyncThunk(
  'locations/fetchTowerLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) throw new Error("User organization not found.");
      if (permissions?.viewTowers !== true) return [];

      // Fetch from the correct Firestore path: towers collection
      const towersRef = collection(db, 'towers');
      const q = query(towersRef, where("organizationId", "==", orgId));
      const querySnapshot = await getDocs(q);
      
      const towers = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Convert GeoPoint to coordinates object
        let coordinates = { lat: 0, lng: 0 };
        if (data.location && data.location.latitude && data.location.longitude) {
          coordinates = {
            lat: data.location.latitude,
            lng: data.location.longitude
          };
        }
        
        return {
          id: doc.id,
          name: data.name || 'Unknown Tower',
          coordinates: coordinates,
          radius: data.radius || 300,
          render_type: 'tower',
          beach: data.location_of_beach || 'N/A',
          lg_count: data.lg_count || 0,
          inventory: data.inventory || [],
          Vehicle_data: data.Vehicle_data || {}
        };
      });

      console.log('Successfully fetched towers:', towers);
      return towers;
    } catch (error) {
      console.error('Could not fetch towers:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBeachLocations = createAsyncThunk(
  'locations/fetchBeachLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) throw new Error("User organization not found.");
      if (permissions?.viewGeofences !== true && !Array.isArray(permissions?.viewGeofences)) return [];

      // Fetch from the correct Firestore path: geofences collection
      const beachesRef = collection(db, 'geofences');
      const q = query(beachesRef, where("organizationId", "==", orgId));
      const querySnapshot = await getDocs(q);
      
      const beaches = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || 'Unknown Beach',
          polygon: data.polygon || [],
          render_type: 'polygon',
          Vehicle_data: data.Vehicle_data || {}
        };
      });

      console.log('Successfully fetched beaches:', beaches);
      return beaches;
    } catch (error) {
      console.error('Could not fetch beaches:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOfficeLocations = createAsyncThunk(
  'locations/fetchOfficeLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) throw new Error("User organization not found.");
      if (permissions?.viewOffices !== true) return [];

      // Fetch from the correct Firestore path: offices collection
      const officesRef = collection(db, 'offices');
      const q = query(officesRef, where("organizationId", "==", orgId));
      const querySnapshot = await getDocs(q);
      
      const offices = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Convert GeoPoint to coordinates object
        let coordinates = { lat: 0, lng: 0 };
        if (data.location && data.location.latitude && data.location.longitude) {
          coordinates = {
            lat: data.location.latitude,
            lng: data.location.longitude
          };
        }
        
        return {
          id: doc.id,
          name: data.name || 'Unknown Office',
          coordinates: coordinates,
          radius: data.radius || 300,
          area: data.area || 'N/A',
          category: data.category || 'other',
          Vehicle_data: data.Vehicle_data || {}
        };
      });

      console.log('Successfully fetched offices:', offices);
      return offices;
    } catch (error) {
      console.error('Could not fetch offices:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTritonLocations = createAsyncThunk(
  'locations/fetchTritonLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) throw new Error("User organization not found.");
      
      // Check permissions for Triton devices - now using separate viewTriton permission
      const canViewTriton = permissions?.viewTriton === true;
      
      console.log('🔍 DEBUG: Permission check for Triton:');
      console.log('  - viewTriton:', permissions?.viewTriton);
      console.log('  - canViewTriton:', canViewTriton);
      
      if (!canViewTriton) {
        console.log('❌ User does not have permission to view Triton devices');
        return [];
      }
      
      console.log('✅ User has permission to view Triton devices');

      // Fetch from the triton collection in Firestore
      const tritonRef = collection(db, 'triton');
      const q = query(tritonRef, where("organizationId", "==", orgId));
      const querySnapshot = await getDocs(q);
      
      const tritonDevices = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Convert GeoPoint to coordinates object
        let coordinates = { lat: 0, lng: 0 };
        if (data.location && data.location.latitude && data.location.longitude) {
          coordinates = {
            lat: data.location.latitude,
            lng: data.location.longitude
          };
        }
        
        return {
          id: doc.id,
          name: data.name || data.deviceId || 'Unknown Triton',
          coordinates: coordinates,
          device_type: data.device_type || 'triton',
          assetType: data.assetType || 'triton',
          triton_type: data.triton_type || 'VTS',
          battery: data.battery || 0,
          status: data.status || 'unknown',
          lastSeen: data.lastSeen,
          organizationId: data.organizationId,
          Vehicle_data: data.Vehicle_data || {}
        };
      });

      console.log('Successfully fetched triton devices:', tritonDevices);
      return tritonDevices;
    } catch (error) {
      console.error('Could not fetch triton devices:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAurusLocations = createAsyncThunk(
  'locations/fetchAurusLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) {
        throw new Error("User organization not found.");
      }
      
      // Check permissions for Aurus devices - now using separate viewAurus permission
      const canViewAurus = permissions?.viewAurus === true;
      
      if (!canViewAurus) {
        return [];
      }

      // Fetch from the aurus collection in Firestore
      const aurusRef = collection(db, 'aurus');
      // Remove organization filter since all Aurus devices belong to the same org
      const querySnapshot = await getDocs(aurusRef);
      
      const aurusDevices = querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Handle coordinates - support both location object and direct fields
        let coordinates = { lat: 0, lng: 0 };
        if (data.location && data.location.latitude && data.location.longitude) {
          // New format with location object
          coordinates = {
            lat: data.location.latitude,
            lng: data.location.longitude
          };
        } else if (data.latitude && data.longitude) {
          // Current format with direct fields
          coordinates = {
            lat: data.latitude,
            lng: data.longitude
          };
        }

        // Use document ID as device ID (C5101, C5201, etc.)
        const deviceId = doc.id;

        // Handle lastSeen - it's a DatetimeWithNanoseconds object
        let lastSeen = null;
        if (data.lastSeen) {
          if (data.lastSeen.toDate) {
            // Firebase Timestamp
            lastSeen = data.lastSeen.toDate().toISOString();
          } else if (data.lastSeen instanceof Date) {
            // Already a Date object
            lastSeen = data.lastSeen.toISOString();
          } else if (typeof data.lastSeen === 'string') {
            // String timestamp
            lastSeen = new Date(data.lastSeen).toISOString();
          }
        }

        return {
          id: deviceId,
          device_id: deviceId,  // Add device_id for sidebar compatibility
          name: data.name || `Aurus ${deviceId}`,
          coordinates: coordinates,
          device_type: 'aurus',
          assetType: 'aurus',
          battery: data.battery || 0,
          battery_level: data.battery || 0,  // Add battery_level for sidebar
          status: data.status || 'offline', // Default to 'offline' instead of 'unknown'
          lastSeen: lastSeen,
          organizationId: data.organizationId,
          deviceId: deviceId,  // Ensure deviceId is set correctly
          Vehicle_data: data.Vehicle_data || {}
        };
      });

      return aurusDevices;
    } catch (error) {
      console.error('Could not fetch aurus devices:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAdafruitLocations = createAsyncThunk(
  'locations/fetchAdafruitLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) {
        console.warn('fetchAdafruitLocations: auth.organization.id is missing; still loading Firestore adafruit collection.');
      }

      if (!canViewAdafruitLocations(permissions)) {
        return [];
      }

      const adafruitRef = collection(db, 'adafruit');
      let querySnapshot = null;

      // Try org-scoped query first (works with restrictive Firestore rules).
      if (orgId) {
        try {
          const scopedQuery = query(adafruitRef, where("organizationId", "==", orgId));
          const scopedSnapshot = await getDocs(scopedQuery);
          if (scopedSnapshot.docs.length > 0) {
            querySnapshot = scopedSnapshot;
          }
        } catch (scopedError) {
          console.warn('fetchAdafruitLocations: org-scoped query failed, trying unscoped read:', scopedError?.message);
        }
      }

      // Fallback for datasets where adafruit docs do not carry organizationId.
      if (!querySnapshot) {
        querySnapshot = await getDocs(adafruitRef);
      }

      const adafruitDevices = mapAdafruitSnapshotToDevices(querySnapshot, orgId);

      if (process.env.NODE_ENV === 'development') {
        console.log(`fetchAdafruitLocations: loaded ${adafruitDevices.length} document(s) from adafruit collection`);
      }

      return adafruitDevices;
    } catch (error) {
      console.error('Could not fetch adafruit devices:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSeaHorseLocations = createAsyncThunk(
  'locations/fetchSeaHorseLocations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const permissions = auth.permissions;
      const orgId = auth.organization?.id;

      if (!orgId) {
        throw new Error("User organization not found.");
      }
      
      // Check permissions for Sea Horse devices - using viewVehicles permission
      const canViewSeaHorse = permissions?.viewVehicles === true || 
                             (Array.isArray(permissions?.viewVehicles) && 
                              permissions.viewVehicles.includes('seahorse'));
      
      if (!canViewSeaHorse) {
        return [];
      }

      // Static Sea Horse locations as provided
      const seaHorseLocations = [
        { lat: 15.398402, lng: 73.870451 },
        { lat: 15.398426, lng: 73.870746 },
        { lat: 15.398301, lng: 73.870641 },
        { lat: 15.398556, lng: 73.870536 },
        { lat: 15.398204, lng: 73.870515 },
        { lat: 15.398231, lng: 73.870501 }
      ];

      const seaHorseDevices = seaHorseLocations.map((location, index) => {
        const deviceId = `seahorse_${index + 1}`;
        const displayName = `Sea Horse ${index + 1}`;
        
        return {
          id: deviceId,
          device_id: deviceId,
          name: displayName,
          displayName: displayName, // Add displayName for nameplates
          coordinates: location,
          device_type: 'seahorse',
          assetType: 'seahorse',
          battery: 100, // Default battery level
          battery_level: 100,
          status: 'active', // Default status
          lastSeen: new Date().toISOString(),
          organizationId: orgId,
          deviceId: deviceId,
          Vehicle_data: {}
        };
      });

      console.log('Successfully fetched sea horse devices:', seaHorseDevices);
      return seaHorseDevices;
    } catch (error) {
      console.error('Could not fetch sea horse devices:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  towerLocations: [],
  beachLocations: [],
  officeLocations: [],
  tritonLocations: [],
  aurusLocations: [],
  adafruitLocations: [],
  seaHorseLocations: [],
  loading: {
    towers: false,
    beaches: false,
    offices: false,
    triton: false,
    aurus: false,
    adafruit: false,
    seaHorse: false,
  },
  error: {
    towers: null,
    beaches: null,
    offices: null,
    triton: null,
    aurus: null,
    adafruit: null,
    seaHorse: null,
  },
};

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    clearAllErrors: (state) => {
      state.error = { towers: null, beaches: null, offices: null, triton: null, aurus: null, adafruit: null, seaHorse: null };
    },
    setAdafruitLocations: (state, action) => {
      state.adafruitLocations = action.payload;
      state.loading.adafruit = false;
      state.error.adafruit = null;
    },
    setBeachLocations: (state, action) => {
      state.beachLocations = action.payload;
      state.loading.beaches = false;
      state.error.beaches = null;
    },
  },
  extraReducers: (builder) => {
    // Helper to generate reducer cases
    const addAssetCases = (thunk, assetKey) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading[assetKey] = true;
          state.error[assetKey] = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading[assetKey] = false;
          state[`${assetKey}Locations`] = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading[assetKey] = false;
          state.error[assetKey] = action.payload;
        });
    };

    addAssetCases(fetchTowerLocations, 'tower');
    addAssetCases(fetchBeachLocations, 'beach');
    addAssetCases(fetchOfficeLocations, 'office');
    addAssetCases(fetchTritonLocations, 'triton');
    addAssetCases(fetchAurusLocations, 'aurus');
    addAssetCases(fetchAdafruitLocations, 'adafruit');
    addAssetCases(fetchSeaHorseLocations, 'seaHorse');
  },
});

export const { clearAllErrors, setAdafruitLocations, setBeachLocations } = locationsSlice.actions;

export default locationsSlice.reducer;
